/**
 * Referral / acquisition tracker.
 *
 * Captures — once per session — the landing URL, the referrer, and any UTM
 * parameters so we can attribute signups and payments back to where the
 * visitor came from. Also tracks the last visited internal path so we
 * know which page the user was on immediately before hitting signup or
 * checkout.
 *
 * All storage uses sessionStorage (per-tab, cleared on tab close). We do
 * NOT persist across sessions because that would be creepy + creates
 * consent-management burden we don't need.
 */

const SS_LANDING_PATH = 'blz.ref.landing_path';
const SS_LANDING_REF = 'blz.ref.landing_referrer';
const SS_UTM_SOURCE = 'blz.ref.utm_source';
const SS_UTM_MEDIUM = 'blz.ref.utm_medium';
const SS_UTM_CAMPAIGN = 'blz.ref.utm_campaign';
const SS_LAST_PATH = 'blz.ref.last_path';
// A/B experiment assignments live under a common prefix so
// ``getReferralContext`` can enumerate them without a hard-coded list.
const SS_AB_PREFIX = 'blz.ab.';

const isBrowser = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * Called on first mount by ReferralTrackerBoot. Records landing + UTMs
 * if we haven't seen them this session yet.
 */
export function initReferralTracking() {
  if (!isBrowser()) return;
  try {
    const ss = window.sessionStorage;
    if (!ss.getItem(SS_LANDING_PATH)) {
      const path = window.location.pathname + window.location.search;
      ss.setItem(SS_LANDING_PATH, path.slice(0, 512));
    }
    if (!ss.getItem(SS_LANDING_REF)) {
      const ref = (document.referrer || '').trim();
      if (ref) ss.setItem(SS_LANDING_REF, ref.slice(0, 512));
    }
    const usp = new URLSearchParams(window.location.search || '');
    const utmS = usp.get('utm_source');
    const utmM = usp.get('utm_medium');
    const utmC = usp.get('utm_campaign');
    if (utmS && !ss.getItem(SS_UTM_SOURCE)) ss.setItem(SS_UTM_SOURCE, utmS.slice(0, 96));
    if (utmM && !ss.getItem(SS_UTM_MEDIUM)) ss.setItem(SS_UTM_MEDIUM, utmM.slice(0, 96));
    if (utmC && !ss.getItem(SS_UTM_CAMPAIGN)) ss.setItem(SS_UTM_CAMPAIGN, utmC.slice(0, 96));
  } catch (_) {
    /* sessionStorage blocked — silently degrade */
  }
}

/**
 * Called on every React Router path change to keep last_path fresh.
 */
export function trackPath(pathname) {
  if (!isBrowser() || !pathname) return;
  try {
    window.sessionStorage.setItem(SS_LAST_PATH, String(pathname).slice(0, 512));
  } catch (_) {
    /* noop */
  }
}

/**
 * Build a compact payload for backend telemetry / signup / checkout calls.
 * All fields are optional.
 */
export function getReferralContext() {
  if (!isBrowser()) return {};
  try {
    const ss = window.sessionStorage;
    const out = {};
    const lp = ss.getItem(SS_LANDING_PATH);
    const last = ss.getItem(SS_LAST_PATH);
    const ref = ss.getItem(SS_LANDING_REF);
    const us = ss.getItem(SS_UTM_SOURCE);
    const um = ss.getItem(SS_UTM_MEDIUM);
    const uc = ss.getItem(SS_UTM_CAMPAIGN);
    if (lp) out.landing_path = lp;
    if (last) out.last_path = last;
    if (ref) out.external_referrer = ref;
    if (us) out.utm_source = us;
    if (um) out.utm_medium = um;
    if (uc) out.utm_campaign = uc;
    // Enumerate all A/B assignments the visitor has been bucketed into
    // this session so downstream signup / checkout events auto-carry
    // the variant.
    const ab = {};
    for (let i = 0; i < ss.length; i++) {
      const k = ss.key(i);
      if (k && k.startsWith(SS_AB_PREFIX)) {
        const v = ss.getItem(k);
        if (v) ab[k.slice(SS_AB_PREFIX.length)] = v;
      }
    }
    if (Object.keys(ab).length) out.ab_variants = ab;
    return out;
  } catch (_) {
    return {};
  }
}


/**
 * Sticky A/B bucketing. On first call for a given ``experimentKey`` the
 * caller is randomly assigned one of the supplied ``variants`` (uniform
 * split, i.e. 50/50 for two variants). The assignment is persisted in
 * sessionStorage so it stays stable across React re-renders and route
 * changes in the same tab. Subsequent calls return the same variant.
 *
 * The variant is also automatically surfaced in
 * ``getReferralContext().ab_variants`` so every downstream signup /
 * checkout event carries the assignment for outcome attribution — no
 * new backend endpoint needed.
 *
 * @param {string} experimentKey  Short slug e.g. 'recruiter_headline_v1'
 * @param {string[]} variants     Non-empty list of variant names
 * @returns {string}              The assigned variant (first entry as safe fallback in SSR / storage-blocked environments)
 */
export function getOrAssignABVariant(experimentKey, variants) {
  const fallback = Array.isArray(variants) && variants.length ? variants[0] : '';
  if (!experimentKey || !fallback) return fallback;
  if (!isBrowser()) return fallback;
  try {
    const ss = window.sessionStorage;
    const storageKey = SS_AB_PREFIX + experimentKey;
    const existing = ss.getItem(storageKey);
    if (existing && variants.includes(existing)) return existing;
    const picked = variants[Math.floor(Math.random() * variants.length)] || fallback;
    ss.setItem(storageKey, picked);
    return picked;
  } catch (_) {
    return fallback;
  }
}
